import React from 'react';
import { ArrowDown, ArrowUp, Copy, Trash2 } from 'lucide-react';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useScene } from '@/hooks/useScene';
import { generateId, getTilePosition } from '@/utils';
import { SceneLayer } from '@/components/SceneLayer/SceneLayer';
import { RenderLayer } from '@/core/renderer/types';
import { PROJECTED_TILE_SIZE } from '@/config';

export const SelectionQuickActions: React.FC = () => {
  const selection = useUiStateStore((state) => state.itemControls);
  const contextMenu = useUiStateStore((state) => state.contextMenu);
  const mode = useUiStateStore((state) => state.mode);
  const actions = useUiStateStore((state) => state.actions);
  const scene = useScene();

  if (!selection || selection.type === 'ADD_ITEM' || selection.type === 'MULTI_SELECT' || mode.type !== 'CURSOR') return null;

  const tile = selection.type === 'ITEM'
    ? scene.items.find((item) => item.id === selection.id)?.tile
    : selection.type === 'TEXTBOX'
      ? scene.textBoxes.find((item) => item.id === selection.id)?.tile
      : selection.type === 'RECTANGLE'
        ? scene.rectangles.find((item) => item.id === selection.id)?.from
        : undefined;
  if (!tile) return null;
  const position = getTilePosition({ tile, origin: 'CENTER' });
  const menuPosition = getTilePosition({ tile, origin: 'TOP' });

  const closeMenu = () => actions.setContextMenu(null);

  const deleteSelection = () => {
    if (selection.type === 'ITEM') {
      scene.deleteViewItem(selection.id);
      scene.deleteModelItem(selection.id);
    } else if (selection.type === 'TEXTBOX') scene.deleteTextBox(selection.id);
    else if (selection.type === 'RECTANGLE') scene.deleteRectangle(selection.id);
    else if (selection.type === 'CONNECTOR') scene.deleteConnector(selection.id);
    actions.setItemControls(null);
    closeMenu();
  };

  const duplicate = () => {
    const layer = document.querySelector('[data-interaction-layer="true"]');
    layer?.dispatchEvent(new KeyboardEvent('keydown', { key: 'd', ctrlKey: true, bubbles: true }));
    closeMenu();
  };

  const startConnector = () => {
    if (selection.type !== 'ITEM' || !scene.colors[0]) return;
    const id = generateId();
    scene.createConnector({
      id,
      color: scene.colors[0].value,
      anchors: [
        { id: generateId(), ref: { item: selection.id } },
        { id: generateId(), ref: { item: selection.id } }
      ]
    });
    actions.setMode({ type: 'CONNECTOR', id, showCursor: true });
  };

  const showMenu = Boolean(
    contextMenu &&
    contextMenu.item.type === selection.type &&
    contextMenu.item.id === selection.id
  );
  const buttonClass = 'rounded-md p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white';

  return (
    <SceneLayer order={RenderLayer.TRANSFORM_CONTROLS + 1} disableAnimation>
      {showMenu && (
        <div
          data-editor-ui="true"
          className="absolute z-[115] flex -translate-x-1/2 -translate-y-full items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
          style={{ left: menuPosition.x, top: menuPosition.y - 12 }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button className={buttonClass} onClick={duplicate} title="Duplicate (Ctrl+D)" aria-label="Duplicate"><Copy size={16} /></button>
          <button className={buttonClass} onClick={() => { scene.changeLayerOrder('BRING_FORWARD', selection); closeMenu(); }} title="Bring forward" aria-label="Bring forward"><ArrowUp size={16} /></button>
          <button className={buttonClass} onClick={() => { scene.changeLayerOrder('SEND_BACKWARD', selection); closeMenu(); }} title="Send backward" aria-label="Send backward"><ArrowDown size={16} /></button>
          <button className={`${buttonClass} hover:!text-red-600`} onClick={deleteSelection} title="Delete" aria-label="Delete"><Trash2 size={16} /></button>
        </div>
      )}
      {selection.type === 'ITEM' && (
        <>
          {[
            { x: -PROJECTED_TILE_SIZE.width / 4, y: -PROJECTED_TILE_SIZE.height / 4 },
            { x: PROJECTED_TILE_SIZE.width / 4, y: -PROJECTED_TILE_SIZE.height / 4 },
            { x: PROJECTED_TILE_SIZE.width / 4, y: PROJECTED_TILE_SIZE.height / 4 },
            { x: -PROJECTED_TILE_SIZE.width / 4, y: PROJECTED_TILE_SIZE.height / 4 }
          ].map((offset, index) => (
            <button
              key={`${offset.x}-${offset.y}`}
              data-editor-ui="true"
              onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); startConnector(); }}
              className="absolute z-[116] h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-500 shadow-md transition hover:scale-125 hover:bg-blue-600"
              style={{ left: position.x + offset.x, top: position.y + offset.y }}
              title="Drag to connect"
              aria-label={`Create connector from handle ${index + 1}`}
            />
          ))}
        </>
      )}
    </SceneLayer>
  );
};
